// Notification bell used when Firefly is docked. Opens the same panel as the
// floating Firefly tab and inherits its glow.
import { useEffect, useState } from "react";
import { ActivityBannerPopover } from "@/components/shell/popovers/ActivityBannerPopover";
import type { FireflyDisplayMode } from "@/lib/firefly/fireflyDisplayMode";
import { dispatchActivityDockOpen } from "@/components/activity/activityDockOpenEvent";
import { resolveFireflyIntent } from "@/components/activity/firefly/resolveFireflyIntent";
import { buildSidebarFireflyDockGlowStyle } from "@/lib/firefly/sidebarFireflyDockGlow";
import { useAttentionChrome } from "@/lib/attention/attentionChrome";
import { useWorkplaceMessagesState } from "@/components/messages/useWorkplaceMessagesState";

export function AttentionBell() {
  const chrome = useAttentionChrome();
  const wp = useWorkplaceMessagesState();
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReducedMotion(media.matches);
    apply();
    media.addEventListener?.("change", apply);
    return () => media.removeEventListener?.("change", apply);
  }, []);

  const intent = resolveFireflyIntent(chrome.tone);
  const glow = intent === "focus" ? undefined : buildSidebarFireflyDockGlowStyle({ intent, reducedMotion });
  const needsYou = chrome.tone != null && chrome.summary.trim().length > 0;
  const unread = wp.unreadCount > 0 ? wp.unreadCount : wp.unackedCount;
  const badge = needsYou ? chrome.summary : unread > 0 ? String(unread) : "";
  const label = chrome.reasonLabel
    ? `Activity — ${chrome.reasonLabel}`
    : unread > 0
      ? `Activity — ${unread} unread`
      : "Activity";

  return (
    <button
      type="button"
      data-attention-bell="1"
      aria-label={label}
      aria-expanded={chrome.panelOpen}
      aria-haspopup="dialog"
      title={chrome.reasonLabel ?? "Activity"}
      onClick={() => dispatchActivityDockOpen({ force: true, toggle: true })}
      style={glow}
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-gray-100"
    >
      <span aria-hidden>🔔</span>
      {badge ? (
        <span className="absolute -right-1 -top-1 min-w-[1.1rem] rounded-full bg-amber-600 px-1 text-[10px] font-semibold leading-4 text-white">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

export function AttentionChrome(props: {
  mode: FireflyDisplayMode;
  fireflyAvailable: boolean;
  allowTestNotification: boolean;
  allowSenderLock: boolean;
  allowRequiresAck: boolean;
  onOpenAdsIngestion?: () => void;
}) {
  if (props.fireflyAvailable && props.mode === "floating") return null;
  if (props.fireflyAvailable && props.mode === "docked") return <AttentionBell />;
  return (
    <ActivityBannerPopover
      allowTestNotification={props.allowTestNotification}
      allowSenderLock={props.allowSenderLock}
      allowRequiresAck={props.allowRequiresAck}
      onOpenAdsIngestion={props.onOpenAdsIngestion}
    />
  );
}
